import { mapState, mapGetters, mapActions } from 'vuex'
import CONSTANTS from '@/constants'
import { eventsConstants } from './eventsList'

const mixpanelEvent = {
  computed: {
    eventsConstants() {
      return eventsConstants
    },
  },
  methods: {
    sendMixpanelEvent(event, payload) {
      this.$store.dispatch('events/sendMixpanelEvent', {
        event_name: event,
        properties: {
          ...payload,
        },
      })
    },
    sendGAEvent(event, payload) {
      this.$store.dispatch('events/sendGAEvent', {
        event,
        ...payload,
      })
    },
  },
}

export const priceDetailUpdates = {
  data() {
    return {
      checkoutPricing: '0.00',
      discount: '0.00',
      subtotal: '0.00',
      taxes: '0.00',
      total: '0.00',
      currencySymbol: '$',
      isPromoAvailable: false,
      currentCoupon: {},
      couponError: null,
      currentDiscount: 0,
      planCodes: Object.values(CONSTANTS.PLANS),
    }
  },
  computed: {
    ...mapState({
      checkoutDiscount: state => state.subscriptions.checkoutDiscount,
      customCoupon: state => state.shoppingCart.coupon,
      dataLayer: state => state.events.dataLayer,
      paymentEntries: state => state.subscriptions.paymentEntries,
      shoppingCart: state => state.shoppingCart.shoppingCart,
      userProfile: state => state.auth.userProfile,
    }),
    ...mapGetters({
      selectedPlan: 'shoppingCart/selectedPlan',
    }),
    coupon: {
      set(value) {
        this.$store.dispatch('shoppingCart/updateCoupon', value)
      },
      get() {
        return this.customCoupon
      },
    },
    selectedPlanCode() {
      return this.selectedPlan?.merchant_id ?? ''
    },
    isFreeTrial() {
      return !!this.selectedPlan?.plan_trial
    },
  },
  methods: {
    addCoupon() {
      this.couponError = null
      this.currentCoupon = this.coupon
      this.handleCouponInformation()
    },
    removeCoupon() {
      this.coupon = ''
      this.currentCoupon = {}
      this.couponError = null
      this.setupPricing()
    },
    async handleCouponInformation() {
      const couponHasError = () => {
        this.couponError = true
        this.couponSpinner = false
      }
      try {
        this.couponError = null
        this.couponSpinner = true
        let hasDiscount = false
        let itemWithDiscount = {}
        const prices = this.checkoutPricing.price
        const {
          now: { subscriptions, adjustments },
        } = prices
        const currentTotal = (Number(subscriptions) + Number(adjustments)).toFixed(2)
        const couponInformation = await this.$store.dispatch(
          'subscriptions/getCouponInformation',
          this.customCoupon
        )

        if (couponInformation) {
          const couponItems = [
            ...couponInformation.items.map(i => i.code),
            ...couponInformation.plans.map(i => i.code),
          ]

          this.shoppingCart.find(item => {
            hasDiscount = couponItems.includes(item.merchant_id)
            itemWithDiscount = item
            return couponItems.includes(item.merchant_id)
          })

          if (hasDiscount) {
            if (couponInformation.discount && couponInformation.discount.percent) {
              const { percent } = couponInformation.discount
              const discountToApply = (percent * (itemWithDiscount.price_cents / 100)) / 100
              prices.now.discount = discountToApply.toFixed(2)
              prices.now.total = (currentTotal - discountToApply).toFixed(2)
            } else if (couponInformation.discount && couponInformation.discount.amount) {
              const { amount } = couponInformation.discount
              prices.now.discount = amount.toFixed(2)
              prices.now.total = (currentTotal - amount).toFixed(2)
            }
            this.couponError = false
            this.couponSpinner = false
            this.handlePricing(prices)
          } else {
            couponHasError()
          }
        } else {
          couponHasError()
        }
      } catch (error) {
        this.handleError(error)
        couponHasError()
      }
    },
    handlePricing(prices) {
      const {
        currency: { symbol },
        now: { discount, subscriptions, adjustments, taxes, total },
      } = prices
      const subtotal = (Number(subscriptions) + Number(adjustments)).toFixed(2).toString()
      if (symbol && symbol.length) {
        this.currencySymbol = symbol
      }
      const cartHasAddons = this.shoppingCart.filter(item => item.addOnChecked)
      if (this.isFreeTrial) {
        this.discount = '0.00'
        this.subtotal = cartHasAddons.length > 0 ? adjustments : '0.00'
        this.taxes = '0.00'
        this.total = cartHasAddons.length > 0 ? adjustments : '0.00'
        const freeTrialTotal = cartHasAddons.length > 0 ? adjustments : '0.00'
        this.$store.dispatch('subscriptions/updateCheckoutDiscount', freeTrialTotal)
        this.$store.dispatch('subscriptions/updateCheckoutTaxes', freeTrialTotal)
        this.$store.dispatch('subscriptions/updateCheckoutTotal', freeTrialTotal)
      } else {
        this.discount = discount
        this.subtotal = subtotal
        this.taxes = taxes
        this.total = total
        this.$store.dispatch('subscriptions/updateCheckoutDiscount', discount)
        this.$store.dispatch('subscriptions/updateCheckoutTaxes', taxes)
        this.$store.dispatch('subscriptions/updateCheckoutTotal', total)
      }
      this.currentDiscount = discount
    },
    calculatePricing() {
      if (this.shoppingCart && this.shoppingCart.length) {
        this.shoppingCart.forEach(item => {
          if (item.merchant_id && this.planCodes.includes(item.merchant_id)) {
            this.createSubscription(item)
              .catch(error => this.handleError(error))
              .done()
          } else {
            this.addSingleProduct(item)
              .catch(error => this.handleError(error))
              .done()
          }
        })
      }
    },
    createSubscription(plan) {
      const { country, zip: postal_code } = this.paymentEntries
      let subscription
      if (plan.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL) {
        subscription = window.recurly.Pricing.Subscription().plan(plan.merchant_id)
        plan.addOnsOnCart.forEach(SDAddOn => subscription.addon(SDAddOn.merchant_id))
      } else {
        subscription = window.recurly.Pricing.Subscription().plan(plan.merchant_id)
      }
      return this.checkoutPricing
        .subscription(subscription.catch(error => this.handleError(error)).done())
        .address({ country, postal_code })
    },
    addSingleProduct(item) {
      return this.checkoutPricing.adjustment({
        id: item.merchant_id,
        itemCode: item.merchant_id,
        quantity: 1,
        taxExempt: false,
      })
    },
    setupPricing() {
      this.currentDiscount = 0
      this.checkoutPricing = window.recurly.Pricing.Checkout()
      this.checkoutPricing.on('change', price => this.handlePricing(price))
      this.calculatePricing()
    },
    handleError(error) {
      console.error('handleError', error)
    },
  },
  watch: {
    shoppingCart(items) {
      this.setupPricing()
    },
    subtotal() {
      if (this.coupon.length > 0) {
        this.addCoupon()
      }
    },
  },
}

export const updateCart = {
  computed: {
    ...mapState({
      shoppingCart: state => state.shoppingCart.shoppingCart,
      availableLiveVirtualTrainings: state => state.products.availableLiveVirtualTrainings,
      liveVirtualTrainingDiscount: state => state.products.liveVirtualTrainingDiscount,
    }),
    ...mapGetters({
      selectedPlan: 'shoppingCart/selectedPlan',
    }),
  },
  methods: {
    ...mapActions({
      addProductToShoppingCart: 'shoppingCart/addProductToShoppingCart',
      removeProductFromShoppingCart: 'shoppingCart/removeProductFromShoppingCart',
    }),
    updateSelectedPlan(plan) {
      const { PLANS, VOUCHERS, LIVE_VIRTUAL_TRAININGS } = CONSTANTS
      const LVTCodes = this.availableLiveVirtualTrainings.map(p => p.merchant_id)
      if (plan.plan_trial) {
        plan.plan_trial = false
      }
      const duplicate = this.shoppingCart.filter(item => item.merchant_id === plan.merchant_id)
      const exists = this.shoppingCart.filter(
        item => item.merchant_id && Object.values(PLANS).includes(item.merchant_id)
      )
      // remove [VOUCHERS.ICCA, VOUCHERS.EJPT]
      // if cart Has PLANS.ANNUAL Subscription
      if (plan.merchant_id === PLANS.ANNUAL) {
        this.shoppingCart.forEach(item => {
          if ([VOUCHERS.ICCA, VOUCHERS.EJPT].includes(item.merchant_id)) {
            this.removeProductFromShoppingCart(item)
          }
        })
      }
      if (exists.length && !duplicate.length) {
        if (plan.merchant_id !== PLANS.SKILL_DIVE_ANNUAL) {
          exists.forEach(item => this.removeProductFromShoppingCart(item))
        }
        if (LVTCodes.includes(plan.merchant_id) && this.liveVirtualTrainingDiscount) {
          const couponToApply = LIVE_VIRTUAL_TRAININGS.COUPONS[plan.merchant_id]
          this.$store.dispatch('shoppingCart/updateCoupon', couponToApply)
        }
        this.addProductToShoppingCart(plan)
      } else if (exists.length || duplicate.length) {
        if (plan.merchant_id !== PLANS.SKILL_DIVE_ANNUAL) {
          exists.forEach(item => item && this.removeProductFromShoppingCart(item))
          duplicate.forEach(item => item && this.removeProductFromShoppingCart(item))
        }
      } else {
        if (LVTCodes.includes(plan.merchant_id) && this.liveVirtualTrainingDiscount) {
          const couponToApply = LIVE_VIRTUAL_TRAININGS.COUPONS[plan.merchant_id]
          this.$store.dispatch('shoppingCart/updateCoupon', couponToApply)
        }
        this.addProductToShoppingCart(plan)
      }
    },
    removeSelectedFromShoppingCart(product) {
      const exists = this.shoppingCart.find(i => i.id === product.id)
      if (exists) {
        // is the item to remove is a LVT, then remove the coupon too
        // logged in users with specific plans need a fixed coupon applied to get the discount
        if (Object.values(CONSTANTS.LIVE_VIRTUAL_TRAININGS).includes(exists.merchant_id)) {
          this.removeCoupon()
        }
        this.removeProductFromShoppingCart(exists)
      }
      if (this.shoppingCart.length > 0) {
        this.setupPricing()
      }
    },
    clearCart() {
      this.shoppingCart.forEach(item => {
        if (item.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL) {
          item.addOnsOnCart = []
          item.selectedAsAddOns = false
          item.price_cents = item.priceToShow
        }
        this.removeProductFromShoppingCart(item)
      })
    },
  },
}

export default mixpanelEvent
